// The shared table's face: the kit list look (Spark's project list) as class strings, by the
// surface the table sits on and by density. docs/features/shared-table.md, "The face".

import type React from 'react';
import { EASE, MS } from '../ui/motion';

/** The surface the table sits on. Rows step one tone up from it; totals and gaps are it. */
export type TableOn = 'ground' | 'panel';
export type TableDensity = 'comfortable' | 'compact';

interface Tones {
  /** Sets --dt-backdrop, which the totals, footer and corner fillets paint with. */
  backdrop: string;
  /** Sets --dt-head, the header band: a tone step above the rows, opaque under a held header. */
  head: string;
  /** Row tones, at zero specificity so a page's own row tint (rowProps) still wins. */
  row: string;
  stripe: string;
  /** The same two tones at full strength, for cells that must be opaque (a pinned column). */
  solidRow: string;
  solidStripe: string;
  /** An opened row's detail (renderSubRow), one step above the rows. */
  sub: string;
}

export const TONES: Record<TableOn, Tones> = {
  ground: {
    backdrop: '[--dt-backdrop:var(--surface)]',
    head: '[--dt-head:var(--surface-container-high)]',
    row: '[:where(&)]:bg-surface-container-low',
    stripe: '[:where(&)]:bg-surface-container',
    solidRow: 'bg-surface-container-low',
    solidStripe: 'bg-surface-container',
    sub: 'bg-surface-container',
  },
  panel: {
    backdrop: '[--dt-backdrop:var(--surface-container-low)]',
    head: '[--dt-head:var(--surface-container-high)]',
    row: '[:where(&)]:bg-surface-container',
    stripe: '[:where(&)]:bg-surface-container-high',
    solidRow: 'bg-surface-container',
    solidStripe: 'bg-surface-container-high',
    sub: 'bg-surface-container-high',
  },
};

const BACKDROP = 'bg-[var(--dt-backdrop)]';

/** The column names: the standard's section label, on the header band. */
const HEAD_LABEL = 'bg-[var(--dt-head)] text-[11px] font-medium uppercase tracking-wider leading-4 text-on-surface-variant whitespace-nowrap';

export const HEADER_CELL: Record<TableDensity, string> = {
  comfortable: `${HEAD_LABEL} px-4 py-2`,
  compact: `${HEAD_LABEL} px-3 py-1.5`,
};

export const CELL: Record<TableDensity, string> = {
  comfortable: 'px-4 py-2.5',
  compact: 'px-3 py-1.5',
};

export const FOOTER_CELL: Record<TableDensity, string> = {
  comfortable: `${BACKDROP} px-4 pb-1.5 pt-2 font-semibold text-on-surface`,
  compact: `${BACKDROP} px-3 pb-1 pt-1.5 font-semibold text-on-surface`,
};

/** A group band names its section in the accent, so a splitter reads as a heading, not a row. */
export const BAND_CELL: Record<TableDensity, string> = {
  comfortable: 'px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider leading-4 text-primary-ink',
  compact: 'px-3 py-1 text-[11px] font-semibold uppercase tracking-wider leading-4 text-primary-ink',
};

export const TABLE_TEXT: Record<TableDensity, string> = {
  comfortable: 'w-full text-sm',
  compact: 'w-full text-[13px] leading-[18px]',
};

export const STRIP: Record<TableDensity, string> = {
  comfortable: 'flex items-center gap-3 min-w-0 px-4 pb-2 pt-1',
  compact: 'flex items-center gap-3 min-w-0 px-3 pb-1.5 pt-1',
};

export const FOOTER: Record<TableDensity, string> = {
  comfortable: `flex items-center gap-3 min-w-0 px-4 py-2 sticky bottom-0 ${BACKDROP}`,
  compact: `flex items-center gap-3 min-w-0 px-3 py-1.5 sticky bottom-0 ${BACKDROP}`,
};

/** The group band: the ladder's top step, chosen by Mannie over darker grooves and accent washes. */
export const BAND_ROW = 'bg-surface-container-highest';

/** The thumb's inset ring is drawn in the backdrop, not in index.css's fixed panel tone. Doubled
 *  class so it outranks `.dt-scroll`, which index.css declares after the utilities. */
export const SCROLL_THUMB = '[&&::-webkit-scrollbar-thumb]:[border-color:var(--dt-backdrop)]';

export const ROW_BASE = [
  'np-state',
  '[transition:background-color_120ms_cubic-bezier(0.2,0,0,1),--np-layer_120ms_cubic-bezier(0.2,0,0,1),box-shadow_380ms_cubic-bezier(0.2,0,0,1),opacity_150ms_cubic-bezier(0.3,0,1,1)]',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/70',
].join(' ');

/**
 * A row's transitions while it glides (rowMotion), set inline for the glide's length. ROW_BASE's
 * list plus the move, so a hover or a fade that starts mid-glide still eases. Change both together.
 */
export const ROW_GLIDE = [
  `translate ${MS.spring}ms ${EASE.spring}`,
  `opacity ${MS.exit}ms ${EASE.exit}`,
  `background-color ${MS.quick}ms ${EASE.emphasized}`,
  `--np-layer ${MS.quick}ms ${EASE.emphasized}`,
  `box-shadow ${MS.spring}ms ${EASE.emphasized}`,
].join(', ');

/**
 * ROW_GLIDE without the move, set while rowMotion measures: a translate change lands at once, and a
 * fade already running on a leaving row keeps running. `none` here would snap that row out of sight.
 */
export const ROW_STILL = ROW_GLIDE.split(', ').filter((t) => !t.startsWith('translate')).join(', ');

/**
 * A row that arrives with others in a small change (more than a landing party) fades in where it
 * lands. The keyframe has no end state, so it finishes at the row's own opacity (a page's dim class).
 */
export const ROW_ENTER = 'animate-rowEnter';
/** A row on its way out: it dissolves in place (opacity is inline, so a page's own dim cannot hold it up). */
export const ROW_LEAVE = 'pointer-events-none';
/** A group band fades with the rows it heads. */
export const BAND_MOTION = 'transition-opacity duration-exit ease-exit';

/**
 * A refresh over rows already on screen: they stay, quieter, until the answer lands. The dim waits
 * a beat, so a quick refetch never flickers the table.
 */
export const BODY_BUSY = 'opacity-60 transition-opacity duration-standard ease-emphasized delay-150';
export const BODY_REST = 'opacity-100 transition-opacity duration-exit ease-exit';
/** The working line along the top of the body: held at the field's top edge under a held header. */
export const BUSY_LINE = 'pointer-events-none sticky left-0 top-0 z-[4] h-0';
/** Its track. Under reduced motion the sweep stops and the track itself carries the state. */
export const BUSY_TRACK: Record<'on' | 'off', string> = {
  on: 'absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-primary/20 motion-reduce:bg-primary/60 opacity-100 transition-opacity duration-standard ease-emphasized delay-150',
  off: 'absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-primary/20 opacity-0 transition-opacity duration-exit ease-exit',
};
export const BUSY_SWEEP = 'h-full w-full bg-gradient-to-r from-transparent via-primary to-transparent motion-safe:animate-shimmer motion-reduce:hidden';

/** Keyboard selection: one step up and the accent edge. LineOrder's CARRIED_ROW copies it. */
export const SELECTED_ROW =
  'bg-surface-container-highest shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]';

/** A row that just landed in a pile already on screen settles in. */
export const ROW_LAND = 'animate-rowLand motion-reduce:animate-none';
/** And gives off light for a moment. Raised so the halo paints over its neighbours. */
export const ROW_GLOW = 'relative z-[1] shadow-glow-soft';
export const LANDING_GLOW_MS = 1600;

/** A pinned cell repaints the row's tone (it must be opaque) and its hover layer. */
export const PINNED_CELL =
  'sticky left-0 z-[1] np-state [tr:hover>&]:[--np-layer:var(--np-hover-color)] transition-colors duration-quick ease-emphasized';

export type Corner = 'tl' | 'tr' | 'bl' | 'br';

// Circle centre (the far corner of a 6px box) and where the box sits in the cell.
const FILLET: Record<Corner, [string, string]> = {
  tl: ['100% 100%', 'left top'],
  tr: ['0 100%', 'right top'],
  bl: ['100% 0', 'left bottom'],
  br: ['0 0', 'right bottom'],
};

/**
 * The stack's four outer corners; a shown header band holds the top two. A row's tone lives on
 * the tr so a page's row tint can replace it, and a tr ignores border-radius, so the corner cells
 * paint a 6px backdrop fillet over the row instead. `layer` keeps a pinned cell's own hover layer under the fillets. jsdom drops
 * gradient backgrounds, so the cells also carry `data-corners` for tests to read.
 */
export function cornerStyle(corners: Corner[], layer = false): React.CSSProperties | undefined {
  if (corners.length === 0) return undefined;
  const images = corners.map(
    (c) => `radial-gradient(circle at ${FILLET[c][0]}, transparent 5.5px, var(--dt-backdrop) 6px)`
  );
  const sizes = corners.map(() => '6px 6px');
  const positions = corners.map((c) => FILLET[c][1]);
  if (layer) {
    images.push('linear-gradient(var(--np-layer), var(--np-layer))');
    sizes.push('100% 100%');
    positions.push('0 0');
  }
  return {
    backgroundImage: images.join(', '),
    backgroundSize: sizes.join(', '),
    backgroundPosition: positions.join(', '),
    backgroundRepeat: 'no-repeat',
  };
}

export function mergeStyle(
  a: React.CSSProperties | undefined,
  b: React.CSSProperties | undefined
): React.CSSProperties | undefined {
  if (!a) return b;
  if (!b) return a;
  return { ...a, ...b };
}
